import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { env } from 'env';

export const API_URL = env.REACT_APP_API_URL;

// Cross-system correlation id for the observability hub (SOLUTION-DESIGN
// §9.1). Set once at startup by MytaverseProvider; attached to every
// outgoing request below so it appears in spatial-manager logs and Sentry
// breadcrumbs alongside the same id stamped on the WS payload and Sentry
// scope tag.
let mytaCorrelationId: string | null = null;
// The session join key (websocketSessionId). Sent on every request as
// X-Mytaverse-Session-Id so backend logs stitch to the same session the
// frontend tags on Sentry + the WS payload (MV-1077 log correlation).
let sessionId: string | null = null;
let accessTokenProvider: (() => Promise<string | null> | string | null) | null =
  null;

export const setMytaCorrelationId = (id: string): void => {
  mytaCorrelationId = id;
};

export const setSessionId = (id: string): void => {
  sessionId = id;
};

export const setApiAccessTokenProvider = (
  provider: (() => Promise<string | null> | string | null) | null,
): void => {
  accessTokenProvider = provider;
};

const normalizeBearerToken = (
  token: string | null | undefined,
): string | null => {
  const value = token?.trim();

  if (!value || value === 'undefined' || value === 'null') {
    return null;
  }

  return value.startsWith('Bearer ') ? value : `Bearer ${value}`;
};

/**
 * Resolves the `Authorization` header value for an outgoing request.
 *
 * Exported because the myta-streams seat-lease calls go through `fetch` rather than this axios
 * instance — a seat release must run with `keepalive` on page unload, which axios cannot do — and
 * they must still authenticate as the same participant.
 *
 * @returns The bearer header value, or null when no usable token is available.
 */
export const resolveAuthorizationHeader = async (): Promise<string | null> => {
  if (accessTokenProvider) {
    try {
      const token = await accessTokenProvider();
      const authorization = normalizeBearerToken(token);

      if (authorization) {
        return authorization;
      }
    } catch {
      // Fall back to the legacy cookie path below. The request must never send
      // the literal strings "undefined" or "null" as an Authorization header.
    }
  }

  return normalizeBearerToken(Cookies.get('token'));
};

const $api = axios.create({
  baseURL: API_URL,
});

$api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const authorization = await resolveAuthorizationHeader();

  if (authorization) {
    config.headers.set('Authorization', authorization);
  } else {
    config.headers.delete('Authorization');
  }

  if (mytaCorrelationId) {
    config.headers.set('X-Mytaverse-Correlation-Id', mytaCorrelationId);
  }

  if (sessionId) {
    config.headers.set('X-Mytaverse-Session-Id', sessionId);
  }

  if (config.url?.includes('/ping')) {
    const requestStartTime = Date.now();

    const metadata = {
      requestStartTime,
    };

    config.data = config.data ? { ...config.data, metadata } : { metadata };
  }
  return config;
});

$api.interceptors.response.use((response: AxiosResponse) => {
  if (response.config.url?.includes('/ping')) {
    if (response.config.data) {
      const { metadata } = JSON.parse(response.config.data);
      if (metadata) {
        const duration = Date.now() - metadata.requestStartTime;

        const data = JSON.parse(response.config.data);
        response.data = { ...data, requestDuration: duration };
      }
    }
  }

  return response;
});

export default $api;
